import { useState, useMemo, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { T } from "../utils/theme";
import { formatDate, ENDPOINTS, api } from "../utils/api";
import { useFetch, useModal } from "../hooks";
import {
  Card, Spinner, EmptyState, SectionHeader, SearchInput,
} from "../components/UI";
import {
  Building2, Globe, Mail, Hash, ShieldCheck, ShieldOff, Loader,
  Clock, XCircle, BadgeCheck, X, FileText, Download, Eye,
  Image, File, FileBadge, AlertCircle, ChevronRight,
} from "lucide-react";

// ─── Statuts de vérification ─────────────────────────────────────────────────
const VERIFICATION_STATUS = {
  en_attente: { label: "En attente",  color: "#ffa502", icon: Clock,      bg: "#ffa50218" },
  accepte:    { label: "Vérifié",     color: "#2ed573", icon: BadgeCheck, bg: "#2ed57318" },
  refuse:     { label: "Refusé",      color: "#ff4757", icon: XCircle,    bg: "#ff475718" },
};

// ─── Types de documents ───────────────────────────────────────────────────────
const DOC_LABELS = {
  kbis:             "Extrait Kbis",
  statuts:          "Statuts de société",
  rib:              "RIB / Coordonnées bancaires",
  piece_identite:   "Pièce d'identité",
  assurance:        "Attestation d'assurance",
  contrat:          "Contrat",
  autres:           "Autre document",
};

const getFileIcon = (url = "") => {
  const ext = url.split(".").pop().toLowerCase();
  if (["jpg","jpeg","png","gif","webp","svg"].includes(ext)) return Image;
  if (["pdf"].includes(ext)) return FileBadge;
  return File;
};

const getFileMeta = (url = "") => {
  const ext = url.split(".").pop().toLowerCase();
  const isImage = ["jpg","jpeg","png","gif","webp"].includes(ext);
  const isPdf   = ext === "pdf";
  return { ext, isImage, isPdf };
};

// ─── Normalisation de la réponse API ─────────────────────────────────────────
const normalizeCompany = (c, group) => ({
  ...c,
  email:              c.user?.email            ?? c.email_company ?? "—",
  userId:             c.user?.id               ?? null,
  role:               c.user?.role             ?? "company",
  avatar:             c.user?.avatar           ?? null,
  verified:           c.user?.verified         ?? false,
  docSet:             c.user?.docSet           ?? false,
  statutCompte:       c.user?.statutCompte     ?? null,
  userCreatedAt:      c.user?.createdAt        ?? null,
  website:            c.urlSite                ?? null,
  siret:              c.num_identification     ?? null,
  statutVerification: c.statutVerification    ?? "en_attente",
  totalPrograms:      group?.totalPrograms     ?? 0,
  activePrograms:     group?.activePrograms    ?? 0,
  totalHackers:       group?.totalHackers      ?? 0,
  totalRecompense:    group?.totalRecompense   ?? 0,
  // documents : tableau d'objets { type, url, nom, createdAt }
  // ou objet clé→url, ou tableau d'urls — on normalise tout
  documents:          normalizeDocuments(c.documents ?? c.docs ?? []),
});

const normalizeDocuments = (docs) => {
  if (!docs) return [];
  // Tableau d'objets { type, url, nom?, createdAt? }
  if (Array.isArray(docs)) {
    return docs.map((d, i) =>
      typeof d === "string"
        ? { id: i, type: "autres", url: d, nom: d.split("/").pop() }
        : { id: d.id ?? i, type: d.type ?? "autres", url: d.url ?? d.path ?? "", nom: d.nom ?? d.name ?? d.url?.split("/").pop() ?? "Document", createdAt: d.createdAt }
    ).filter(d => d.url);
  }
  // Objet clé→url  { kbis: "http://...", rib: "http://..." }
  if (typeof docs === "object") {
    return Object.entries(docs)
      .filter(([, v]) => v)
      .map(([type, url], i) => ({
        id: i, type, url,
        nom: DOC_LABELS[type] || type,
      }));
  }
  return [];
};

// ─── KYC Badge ───────────────────────────────────────────────────────────────
const KycBadge = ({ docSet }) =>
  docSet ? (
    <span className="badge" style={{ color: T.green, background: `${T.green}18`, fontWeight: 700 }}>
      <ShieldCheck size={10} /> KYC validé
    </span>
  ) : (
    <span className="badge" style={{ color: T.yellow, background: `${T.yellow}18`, fontWeight: 700 }}>
      <ShieldOff size={10} /> KYC en attente
    </span>
  );

// ─── Verification Badge ──────────────────────────────────────────────────────
const VerificationBadge = ({ statut }) => {
  const cfg = VERIFICATION_STATUS[statut] || VERIFICATION_STATUS.en_attente;
  const Icon = cfg.icon;
  return (
    <span className="badge" style={{ color: cfg.color, background: cfg.bg, fontWeight: 700 }}>
      <Icon size={10} /> {cfg.label}
    </span>
  );
};

// ─── Document Viewer (lightbox minimaliste) ───────────────────────────────────
const DocViewer = ({ doc, onClose }) => {
  if (!doc) return null;
  const { isImage, isPdf } = getFileMeta(doc.url);
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(0,0,0,0.82)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 16, padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.surface || T.bg,
          borderRadius: 16, border: `1px solid ${T.border}`,
          overflow: "hidden", maxWidth: "90vw", maxHeight: "82vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 40px 100px rgba(0,0,0,.5)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{doc.nom || "Document"}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <a
              href={doc.url}
              download
              target="_blank"
              rel="noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 7, border: "none",
                background: T.blue || "#0ea5e9", color: "#fff",
                fontSize: 11, fontWeight: 700, cursor: "pointer", textDecoration: "none",
              }}
            >
              <Download size={12} /> Télécharger
            </a>
            <button onClick={onClose} style={{
              background: `${T.muted}20`, border: "none", cursor: "pointer",
              color: T.muted, width: 32, height: 32, borderRadius: 7,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0a0a" }}>
          {isImage ? (
            <img
              src={doc.url}
              alt={doc.nom}
              style={{ maxWidth: "80vw", maxHeight: "70vh", objectFit: "contain", display: "block" }}
            />
          ) : isPdf ? (
            <iframe
              src={doc.url}
              title={doc.nom}
              style={{ width: "75vw", height: "70vh", border: "none" }}
            />
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: T.muted }}>
              <File size={48} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 13, marginBottom: 16 }}>Aperçu non disponible pour ce format.</p>
              <a
                href={doc.url}
                download
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 18px", borderRadius: 8, border: "none",
                  background: T.blue || "#0ea5e9", color: "#fff",
                  fontSize: 12, fontWeight: 700, textDecoration: "none",
                }}
              >
                <Download size={13} /> Télécharger le fichier
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Documents Section ────────────────────────────────────────────────────────
const DocumentsSection = ({ documents }) => {
  const [viewing, setViewing] = useState(null);

  if (!documents || documents.length === 0) {
    return (
      <div style={{
        background: T.bg, borderRadius: 10, padding: "20px 16px",
        textAlign: "center", marginBottom: 18, border: `1px dashed ${T.border}`,
      }}>
        <AlertCircle size={22} style={{ color: T.muted, marginBottom: 8 }} />
        <div style={{ fontSize: 12, color: T.muted }}>Aucun document envoyé par cette entreprise.</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: T.muted,
          letterSpacing: ".07em", marginBottom: 10,
        }}>
          DOCUMENTS ({documents.length})
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {documents.map((doc) => {
            const { isImage, ext } = getFileMeta(doc.url);
            const DocIcon = getFileIcon(doc.url);
            const label = DOC_LABELS[doc.type] || doc.type || "Document";
            return (
              <div
                key={doc.id}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: T.bg, borderRadius: 10,
                  border: `1px solid ${T.border}`,
                  padding: "10px 14px",
                  transition: "border-color .15s",
                }}
              >
                {/* Miniature ou icône */}
                {isImage ? (
                  <div style={{
                    width: 40, height: 40, borderRadius: 7, overflow: "hidden",
                    flexShrink: 0, border: `1px solid ${T.border}`,
                  }}>
                    <img src={doc.url} alt={doc.nom} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: 7, flexShrink: 0,
                    background: `${T.blue || "#0ea5e9"}15`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid ${T.blue || "#0ea5e9"}30`,
                  }}>
                    <DocIcon size={18} color={T.blue || "#0ea5e9"} />
                  </div>
                )}

                {/* Infos */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 10, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {doc.nom} {ext && <span style={{ textTransform: "uppercase", background: `${T.muted}20`, borderRadius: 3, padding: "0 4px", marginLeft: 4 }}>{ext}</span>}
                  </div>
                  {doc.createdAt && (
                    <div style={{ fontSize: 9, color: T.muted, marginTop: 2 }}>{formatDate(doc.createdAt)}</div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => setViewing(doc)}
                    title="Voir"
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 30, height: 30, borderRadius: 7, border: "none", cursor: "pointer",
                      background: `${T.muted}15`, color: T.muted, transition: "all .15s",
                    }}
                  >
                    <Eye size={13} />
                  </button>
                  <a
                    href={doc.url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    title="Télécharger"
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 30, height: 30, borderRadius: 7, border: "none", cursor: "pointer",
                      background: `${T.blue || "#0ea5e9"}18`, color: T.blue || "#0ea5e9",
                      textDecoration: "none", transition: "all .15s",
                    }}
                  >
                    <Download size={13} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bouton tout télécharger */}
      {documents.length > 1 && (
        <div style={{ marginBottom: 18 }}>
          <button
            onClick={() => documents.forEach(d => {
              const a = document.createElement("a");
              a.href = d.url; a.download = d.nom || "document"; a.target = "_blank";
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
            })}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              width: "100%", padding: "9px 0", borderRadius: 8, border: `1px solid ${T.border}`,
              background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 700,
              color: T.muted, transition: "all .15s",
            }}
          >
            <Download size={13} /> Tout télécharger ({documents.length} fichiers)
          </button>
        </div>
      )}

      {/* Lightbox */}
      {viewing && <DocViewer doc={viewing} onClose={() => setViewing(null)} />}
    </>
  );
};

// ─── Company Card ─────────────────────────────────────────────────────────────
const CompanyCard = ({ company, onView }) => (
  <Card
    className="hov-card"
    style={{ padding: 20, transition: "all .2s", cursor: "pointer" }}
    onClick={() => onView(company)}
  >
    <div style={{ display: "flex", alignItems: "flex-start", gap: 13, marginBottom: 14 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        background: `hsl(${(company.nom || "A").charCodeAt(0) * 7 % 360}, 45%, 30%)`,
        border: `2px solid hsl(${(company.nom || "A").charCodeAt(0) * 7 % 360}, 55%, 40%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 17, fontWeight: 900, color: "#fff",
      }}>
        {(company.nom || company.email || "C")[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
          {company.nom || "—"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.muted }}>
          <Mail size={11} />{company.email}
        </div>
      </div>
      {company.documents?.length > 0 && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: T.blue || "#0ea5e9",
          background: `${T.blue || "#0ea5e9"}15`, borderRadius: 6, padding: "2px 7px",
          display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
        }}>
          <FileText size={9} /> {company.documents.length}
        </span>
      )}
    </div>

    {company.num_identification && (
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textSub, marginBottom: 10 }}>
        <Hash size={11} color={T.muted} /> N° ID: {company.num_identification}
      </div>
    )}

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <VerificationBadge statut={company.statutVerification} />
        <KycBadge docSet={company.docSet} />
        {company.urlSite && (
          <span className="badge" style={{ color: T.cyan, background: `${T.cyan}12` }}>
            <Globe size={9} /> Web
          </span>
        )}
      </div>
      <div style={{ fontSize: 10, color: T.muted }}>{formatDate(company.createdAt)}</div>
    </div>
  </Card>
);

// ─── Tab Button ───────────────────────────────────────────────────────────────
const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer",
      fontSize: 12, fontWeight: 700, transition: "all .15s",
      background: active ? (T.blue || "#0ea5e9") : "transparent",
      color: active ? "#fff" : T.muted,
    }}
  >
    {children}
  </button>
);

// ─── Company Detail Modal ─────────────────────────────────────────────────────
const CompanyDetailModal = ({ company, open, onClose, token, onKycUpdated, onVerificationUpdated }) => {
  const [kycLoading, setKycLoading]   = useState(false);
  const [kycError,   setKycError]     = useState(null);
  const [kycSuccess, setKycSuccess]   = useState(null);

  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError,   setVerifyError]   = useState(null);
  const [verifySuccess, setVerifySuccess] = useState(null);

  const [activeTab, setActiveTab] = useState("infos"); // "infos" | "docs" | "actions" | "stats" | "programs"

  const handleClose = () => {
    setKycError(null); setKycSuccess(null);
    setVerifyError(null); setVerifySuccess(null);
    setActiveTab("infos");
    onClose();
  };

  const handleVerification = useCallback(async (statut) => {
    if (!company) return;
    setVerifyLoading(true); setVerifyError(null); setVerifySuccess(null);
    try {
      const res = await api(
        ENDPOINTS.verifyCompany(company.id),
        { method: "PUT", body: JSON.stringify({ statut }) },
        token
      );
      const labels = { accepte: "acceptée", refuse: "refusée", en_attente: "mise en attente" };
      setVerifySuccess(res.message || `Entreprise ${labels[statut]} avec succès.`);
      onVerificationUpdated(company.id, { statutVerification: statut });
    } catch (err) {
      setVerifyError(err.message || "Une erreur est survenue.");
    } finally { setVerifyLoading(false); }
  }, [company, token, onVerificationUpdated]);

  const handleKyc = useCallback(async (activate) => {
    if (!company) return;
    setKycLoading(true); setKycError(null); setKycSuccess(null);
    try {
      const res = await api(
        ENDPOINTS.validateKYC(company.id),
        { method: "PATCH", body: JSON.stringify({ docSet: activate }) },
        token
      );
      setKycSuccess(res.message || (activate ? "KYC activé avec succès." : "KYC désactivé avec succès."));
      onKycUpdated(company.id, { docSet: activate, verified: activate });
    } catch (err) {
      setKycError(err.message || "Une erreur est survenue.");
    } finally { setKycLoading(false); }
  }, [company, token, onKycUpdated]);

  if (!company && !open) return null;

  const isValidated = company?.docSet;
  const docs = company?.documents || [];

  const fields = [
    { l: "Nom",               v: company?.nom },
    { l: "Email",             v: company?.email },
    { l: "Email company",     v: company?.email_company },
    { l: "Type",              v: company?.type_entreprise },
    { l: "Secteur",           v: company?.secteur },
    { l: "Statut actuel",     v: company?.statut_actuel },
    { l: "Responsable",       v: company?.responsable_nom_complet },
    { l: "Contact",           v: company?.responsable_contact },
    { l: "Adresse",           v: company?.adresse },
    { l: "N° identification", v: company?.num_identification },
    { l: "Site web",          v: company?.website },
    { l: "Créé le",           v: formatDate(company?.createdAt) },
    { l: "ID",                v: company?.id },
  ];

  const modalContent = (
    <>
      {/* Overlay */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(4px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .25s ease",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%", left: "50%",
          transform: open
            ? "translate(-50%, -50%) scale(1)"
            : "translate(-50%, -50%) scale(0.96)",
          zIndex: 1001,
          width: "min(600px, 96vw)",
          maxHeight: "90vh",
          display: "flex", flexDirection: "column",
          borderRadius: 18, border: `1px solid ${T.border}`,
          background: T.surface || T.bg,
          boxShadow: "0 32px 80px rgba(0,0,0,.35), 0 0 0 1px rgba(255,255,255,.04)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .25s ease, transform .28s cubic-bezier(.4,0,.2,1)",
          overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}>
          {/* Ligne 1 : titre + fermer */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                background: `hsl(${(company?.nom || "A").charCodeAt(0) * 7 % 360}, 45%, 30%)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 900, color: "#fff",
              }}>
                {(company?.nom || "C")[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 16 }}>{company?.nom || "—"}</div>
                <div style={{ fontSize: 11, color: T.muted }}>{company?.email}</div>
              </div>
            </div>
            <button onClick={handleClose} style={{
              background: `${T.muted}15`, border: "none", cursor: "pointer",
              color: T.muted, width: 32, height: 32, borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <X size={16} />
            </button>
          </div>

          {/* Badges */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            <VerificationBadge statut={company?.statutVerification} />
            <KycBadge docSet={isValidated} />
            {company?.urlSite && (
              <span className="badge" style={{ color: T.cyan, background: `${T.cyan}12` }}>
                <Globe size={9} /> {company.urlSite}
              </span>
            )}
          </div>

          {/* Tabs */}
          <div style={{
            display: "flex", gap: 4,
            background: `${T.muted}10`, borderRadius: 10, padding: 4,
          }}>
            <TabBtn active={activeTab === "infos"} onClick={() => setActiveTab("infos")}>
              Informations
            </TabBtn>
            <TabBtn active={activeTab === "docs"} onClick={() => setActiveTab("docs")}>
              Documents {docs.length > 0 && `(${docs.length})`}
            </TabBtn>
            <TabBtn active={activeTab === "programs"} onClick={() => setActiveTab("programs")}>
              Programmes {company?.programs?.length > 0 && `(${company.programs.length})`}
            </TabBtn>
            <TabBtn active={activeTab === "actions"} onClick={() => setActiveTab("actions")}>
              Actions
            </TabBtn>
            <TabBtn active={activeTab === "stats"} onClick={() => setActiveTab("stats")}>
              Statistiques
            </TabBtn>
          </div>
        </div>

        {/* ── Contenu scrollable ────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>

          {/* ── TAB : Informations ──────────────────────────────────────────── */}
          {activeTab === "infos" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {fields.filter(f => f.v).map((f, i) => (
                <div key={i} style={{ background: T.bg, borderRadius: 8, padding: "11px 14px" }}>
                  <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 4 }}>
                    {f.l.toUpperCase()}
                  </div>
                  <div style={{ fontSize: 12, color: T.text, wordBreak: "break-all" }}>{f.v}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── TAB : Documents ─────────────────────────────────────────────── */}
          {activeTab === "docs" && (
            <DocumentsSection documents={docs} />
          )}

          {/* ── TAB : Actions ───────────────────────────────────────────────── */}
          {activeTab === "actions" && (
            <>
              {/* KYC */}
              <div style={{
                background: isValidated ? `${T.green}0D` : `${T.yellow}0D`,
                border: `1px solid ${isValidated ? T.green : T.yellow}33`,
                borderRadius: 10, padding: "14px 16px", marginBottom: 16,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: ".07em", marginBottom: 8 }}>
                  VALIDATION KYC
                </div>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
                  {isValidated
                    ? "Cette entreprise a été validée. Le KYC est actif et le compte est vérifié."
                    : "Cette entreprise n'a pas encore été validée. Activez le KYC pour lui donner accès complet."}
                </div>
                {kycSuccess && <AlertBox color={T.green} msg={kycSuccess} ok />}
                {kycError   && <AlertBox color="#ff4757" msg={kycError} />}
                <div style={{ display: "flex", gap: 8 }}>
                  {!isValidated ? (
                    <ActionBtn loading={kycLoading} onClick={() => handleKyc(true)} color={T.green} icon={ShieldCheck}>
                      Valider le KYC
                    </ActionBtn>
                  ) : (
                    <ActionBtn loading={kycLoading} onClick={() => handleKyc(false)} color="#ff4757" icon={ShieldOff}>
                      Révoquer le KYC
                    </ActionBtn>
                  )}
                </div>
              </div>

              {/* Vérification */}
              <div style={{
                background: VERIFICATION_STATUS[company?.statutVerification]?.bg || `${T.yellow}0D`,
                border: `1px solid ${VERIFICATION_STATUS[company?.statutVerification]?.color || T.yellow}33`,
                borderRadius: 10, padding: "14px 16px",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: ".07em" }}>
                    VÉRIFICATION ENTREPRISE
                  </div>
                  <VerificationBadge statut={company?.statutVerification} />
                </div>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
                  {company?.statutVerification === "accepte"
                    ? "Cette entreprise a été vérifiée et validée par un administrateur."
                    : company?.statutVerification === "refuse"
                    ? "Cette entreprise a été refusée."
                    : "Cette entreprise est en attente de vérification."}
                </div>
                {verifySuccess && <AlertBox color={T.green} msg={verifySuccess} ok />}
                {verifyError   && <AlertBox color="#ff4757" msg={verifyError} />}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {company?.statutVerification !== "accepte" && (
                    <ActionBtn loading={verifyLoading} onClick={() => handleVerification("accepte")} color="#2ed573" icon={BadgeCheck}>
                      Accepter
                    </ActionBtn>
                  )}
                  {company?.statutVerification !== "refuse" && (
                    <ActionBtn loading={verifyLoading} onClick={() => handleVerification("refuse")} color="#ff4757" icon={XCircle}>
                      Refuser
                    </ActionBtn>
                  )}
                  {company?.statutVerification !== "en_attente" && (
                    <ActionBtn loading={verifyLoading} onClick={() => handleVerification("en_attente")} color="#ffa502" icon={Clock}>
                      Mettre en attente
                    </ActionBtn>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── TAB : Programmes ────────────────────────────────────────────── */}
          {activeTab === "programs" && (
            <div>
              {company?.programs && company.programs.length > 0 ? (
                company.programs.map((program, idx) => (
                  <div key={program.id || idx} style={{
                    background: T.bg, borderRadius: 10, padding: "16px 18px", marginBottom: 14,
                    border: `1px solid ${T.border}`
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{program.titre}</div>
                        <div style={{ fontSize: 12, color: T.muted }}>{program.description}</div>
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: program.statut === "actif" ? T.green : T.yellow,
                        background: program.statut === "actif" ? `${T.green}18` : `${T.yellow}18`,
                        borderRadius: 6, padding: "4px 8px"
                      }}>
                        {program.statut}
                      </span>
                    </div>
                    
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>INSCOPE</div>
                        <div style={{ fontSize: 12 }}>
                          {program.inscope && program.inscope.length > 0 ? (
                            program.inscope.map((item, i) => (
                              <div key={i} style={{ marginBottom: 4 }}>
                                <strong>{item.type}:</strong> {item.target}
                                {item.description && <div style={{ fontSize: 10, color: T.muted }}>{item.description}</div>}
                              </div>
                            ))
                          ) : "Aucun"}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>OUTSCOPE</div>
                        <div style={{ fontSize: 12 }}>
                          {program.outscope && program.outscope.length > 0 ? (
                            program.outscope.map((item, i) => (
                              <div key={i} style={{ marginBottom: 4 }}>
                                {typeof item === "string" ? item : item.cible || item.target}
                              </div>
                            ))
                          ) : "Aucun"}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
                      {[
                        { label: "Bas", value: program.prix_bas, color: T.green },
                        { label: "Moyen", value: program.prix_moyen, color: T.blue },
                        { label: "Élevé", value: program.prix_eleve, color: T.yellow },
                        { label: "Critique", value: program.prix_critique, color: "#ff4757" }
                      ].map((p, i) => (
                        <div key={i} style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>{p.label}</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: p.color }}>
                            {p.value ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF", maximumFractionDigits: 0 }).format(p.value) : "—"}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 8 }}>
                        RAPPORTS ({program.reports ? program.reports.length : 0})
                      </div>
                      {program.reports && program.reports.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {program.reports.map((report, rIdx) => (
                            <div key={report.id || rIdx} style={{
                              background: T.surface || T.bg, borderRadius: 8, padding: "10px 12px",
                              border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between"
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>{report.titre}</div>
                                <div style={{ fontSize: 10, color: T.muted }}>
                                  {report.hacker ? `${report.hacker.prenom} ${report.hacker.nom}` : "Hacker inconnu"} • 
                                  Statut: {report.statut} • 
                                  CVSS: {report.cvssScore || "—"}
                                </div>
                              </div>
                              <div style={{ fontSize: 10, color: T.muted }}>
                                {formatDate(report.createdAt)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: T.muted, textAlign: "center", padding: "20px" }}>
                          Aucun rapport pour ce programme
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: "center", padding: "40px 20px", color: T.muted }}>
                  <Building2 size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Aucun programme</div>
                  <div style={{ fontSize: 12 }}>Cette entreprise n'a pas encore créé de programmes.</div>
                </div>
              )}
            </div>
          )}

          {/* ── TAB : Statistiques ──────────────────────────────────────────── */}
          {activeTab === "stats" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 14 }}>
              {[
                { l: "Programmes total",  v: company?.totalPrograms,  c: T.blue,   icon: "📁" },
                { l: "Programmes actifs", v: company?.activePrograms, c: T.green,  icon: "✅" },
                { l: "Hackers inscrits",  v: company?.totalHackers,   c: T.purple, icon: "🧑‍💻" },
                { l: "Total récompenses", v: company?.totalRecompense != null
                    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF", maximumFractionDigits: 0 }).format(company.totalRecompense)
                    : "—", c: T.yellow, icon: "💰" },
              ].map((s, i) => (
                <div key={i} style={{ background: T.bg, borderRadius: 10, padding: "16px 18px" }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".06em", marginBottom: 6 }}>{s.l.toUpperCase()}</div>
                  <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 26, color: s.c }}>{s.v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// ─── Helpers UI ───────────────────────────────────────────────────────────────
const AlertBox = ({ color, msg, ok }) => (
  <div style={{
    fontSize: 12, color, background: `${color}15`,
    borderRadius: 6, padding: "8px 12px", marginBottom: 10,
  }}>
    {ok ? "✓" : "✗"} {msg}
  </div>
);

const ActionBtn = ({ loading, onClick, color, icon: Icon, children }) => (
  <button
    disabled={loading}
    onClick={onClick}
    style={{
      display: "flex", alignItems: "center", gap: 6,
      padding: "8px 16px", borderRadius: 8, border: "none",
      background: color, color: "#fff",
      fontSize: 12, fontWeight: 700,
      cursor: loading ? "not-allowed" : "pointer",
      opacity: loading ? 0.7 : 1, transition: "opacity .2s",
    }}
  >
    {loading
      ? <Loader size={13} style={{ animation: "spin 1s linear infinite" }} />
      : <Icon size={13} />}
    {children}
  </button>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const CompaniesPage = ({ token }) => {
  const { data: raw, loading } = useFetch(ENDPOINTS.adminCompanies, token);
  const modal                  = useModal();

  const [modalData, setModalData] = useState(null);
  const [search, setSearch]       = useState("");
  const [kycOverrides, setKycOverrides] = useState({});

  const companies = useMemo(() => {
    if (!Array.isArray(raw)) return [];
    const seen = new Set();
    return raw.flatMap(group => {
      const companyData = group.company;
      if (!companyData) return [];
      const companyArray = Array.isArray(companyData) ? companyData : [companyData];
      return companyArray.map(c => normalizeCompany(c, group));
    }).filter(c => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [raw]);

  const companiesWithOverrides = useMemo(() =>
    companies.map(c => kycOverrides[c.id] ? { ...c, ...kycOverrides[c.id] } : c),
  [companies, kycOverrides]);

  const handleKycUpdated = useCallback((companyId, patch) => {
    setKycOverrides(prev => ({ ...prev, [companyId]: { ...prev[companyId], ...patch } }));
    setModalData(prev => prev?.id === companyId ? { ...prev, ...patch } : prev);
  }, []);

  const handleVerificationUpdated = useCallback((companyId, patch) => {
    setKycOverrides(prev => ({ ...prev, [companyId]: { ...prev[companyId], ...patch } }));
    setModalData(prev => prev?.id === companyId ? { ...prev, ...patch } : prev);
  }, []);

  const list = companiesWithOverrides.filter(c =>
    !search ||
    c.nom?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const thisMonth = companiesWithOverrides.filter(c => {
    if (!c.createdAt) return false;
    const d = new Date(c.createdAt); const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const validatedCount = companiesWithOverrides.filter(c => c.docSet).length;

  const handleView = useCallback((company) => {
    const withOverride = kycOverrides[company.id] ? { ...company, ...kycOverrides[company.id] } : company;
    setModalData(withOverride);
    modal.openWith(withOverride);
  }, [kycOverrides, modal]);

  const handleClose = useCallback(() => {
    modal.close();
    setModalData(null);
  }, [modal]);

  return (
    <div className="fade-up" style={{ padding: "24px 28px", overflowY: "auto", flex: 1, position: "relative" }}>
      <SectionHeader
        title="Entreprises"
        sub={`${companiesWithOverrides.length} entreprise(s) inscrite(s)`}
        action={<SearchInput value={search} onChange={setSearch} style={{ width: 240 }} />}
      />

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
        {[
          { label: "Total",      value: companiesWithOverrides.length, color: T.blue   },
          { label: "Actives",    value: companiesWithOverrides.length, color: T.green  },
          { label: "KYC validé", value: validatedCount,               color: T.cyan   },
          { label: "Ce mois",    value: thisMonth,                    color: T.purple },
        ].map((s, i) => (
          <Card key={i} style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: T.muted }}>{s.label}</span>
            <span style={{ fontFamily: T.display, fontWeight: 900, fontSize: 24, color: s.color }}>{s.value}</span>
          </Card>
        ))}
      </div>

      {loading ? <Spinner /> : list.length === 0 ? (
        <EmptyState icon={Building2} title="Aucune entreprise" sub="Aucun résultat pour cette recherche" />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 14 }}>
          {list.map((c, i) => (
            <CompanyCard key={c.id || i} company={c} onView={handleView} />
          ))}
        </div>
      )}

      <CompanyDetailModal
        company={modalData}
        open={modal.open}
        onClose={handleClose}
        token={token}
        onKycUpdated={handleKycUpdated}
        onVerificationUpdated={handleVerificationUpdated}
      />
    </div>
  );
};

export default CompaniesPage;